// src/pages/community.jsx
import React, { useContext, useMemo } from 'react'
import { AuthContext } from '../contexts/AuthContext'
import { useSidebar } from '../contexts/SidebarContext'
import Button from '../components/Shared/Button'
import {
  UserGroupIcon,
  BeakerIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline'

export default function CommunityPage() {
  const { auth } = useContext(AuthContext)
  const { openLibrary } = useSidebar()

  const firstName = useMemo(() => {
    const raw = auth?.user?.first_name || auth?.user?.name || ''
    const cleaned = String(raw || '').trim()
    return cleaned ? cleaned.split(' ')[0] : ''
  }, [auth])

  return (
    <div className="px-3 sm:px-4 py-6 sm:py-8">
      <div className="max-w-5xl mx-auto space-y-8">

        {/* HERO */}
        <section className="rounded-2xl border border-light-gray bg-white shadow-sm p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <UserGroupIcon className="w-7 h-7 text-[#5a3e36]" />
            <div>
              <div className="text-xs sm:text-sm font-semibold text-[#7a6f66]">
                {firstName ? `Welcome back, ${firstName}.` : 'Welcome back.'}
              </div>

              <h1 className="mt-1 text-2xl sm:text-3xl md:text-4xl font-semibold text-[#5a3e36]">
                Built for people who take food seriously.
              </h1>

              <p className="mt-2 text-sm sm:text-base text-[#7a6f66] max-w-2xl">
                Ruoth is where recipes become a repeatable system and not a pile of notes.
              </p>

              <div className="mt-4 rounded-xl border border-light-gray bg-soft-yellow/5 px-4 py-3 max-w-3xl">
                <div className="text-xs font-semibold text-[#7a6f66]">Our line in the sand</div>
                <div className="mt-1 text-sm sm:text-base font-semibold text-[#5a3e36]">
                  If your recipes matter, your system should too.
                </div>
              </div>

              <div className="mt-4 grid sm:grid-cols-2 gap-3 max-w-3xl">
                <div className="rounded-xl border border-light-gray bg-white p-4">
                  <div className="text-xs font-semibold text-[#7a6f66]">Not built for</div>
                  <ul className="mt-2 space-y-1 text-sm text-[#5a3e36]">
                    <li>• random note-taking</li>
                    <li>• copy–paste chaos</li>
                    <li>• one-off recipes you'll never revisit</li>
                  </ul>
                </div>

                <div className="rounded-xl border border-light-gray bg-white p-4">
                  <div className="text-xs font-semibold text-[#7a6f66]">Built for</div>
                  <ul className="mt-2 space-y-1 text-sm text-[#5a3e36]">
                    <li>• repeatability</li>
                    <li>• clarity and confidence</li>
                    <li>• scaling, costing, and planning</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHO THIS IS FOR */}
        <section className="rounded-2xl border border-light-gray bg-soft-yellow/5 p-6 sm:p-8">
          <h2 className="text-lg sm:text-xl font-semibold text-[#5a3e36]">
            Who this is for
          </h2>

          <ul className="mt-4 grid sm:grid-cols-2 gap-3 text-sm text-[#5a3e36]">
            <li>• Chefs & kitchen teams building consistent menus</li>
            <li>• Bakers & pastry chefs working in ratios</li>
            <li>• Creators selling premium recipes & digital products</li>
            <li>• Food businesses running collections and meal plans</li>
            <li>• Serious home cooks building a personal system</li>
            <li>• <span className="font-semibold">R&D chefs & test kitchens</span> iterating and validating</li>
          </ul>

          <p className="mt-4 text-sm text-[#7a6f66] max-w-3xl">
            If you care about craft, repeatability, and doing things properly you're in the right place.
          </p>

          <div className="mt-5 rounded-xl border border-light-gray bg-white px-4 py-3">
            <div className="text-xs font-semibold text-[#7a6f66]">Used by</div>
            <div className="mt-1 flex flex-wrap gap-2 text-sm text-[#5a3e36]">
              <span className="px-3 py-1 rounded-full border border-light-gray bg-soft-yellow/5">chefs</span>
              <span className="px-3 py-1 rounded-full border border-light-gray bg-soft-yellow/5">bakers</span>
              <span className="px-3 py-1 rounded-full border border-light-gray bg-soft-yellow/5">creators</span>
              <span className="px-3 py-1 rounded-full border border-light-gray bg-soft-yellow/5">food teams</span>
              <span className="px-3 py-1 rounded-full border border-light-gray bg-soft-yellow/5">test kitchens</span>
            </div>
          </div>
        </section>

        {/* WHAT’S COMING (HONEST) */}
        <section className="rounded-2xl border border-light-gray bg-white shadow-sm p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <BeakerIcon className="w-6 h-6 text-charcoal" />
            <h2 className="text-lg sm:text-xl font-semibold text-charcoal">
              What’s shipping next
            </h2>
          </div>

          <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
            <div className="rounded-xl border border-light-gray bg-soft-yellow/5 p-4">
              <div className="font-semibold text-charcoal">Export</div>
              <p className="mt-1 text-slate-gray">
                Clean, client-ready PDFs you can share, sell, and deliver.
              </p>
            </div>

            <div className="rounded-xl border border-light-gray bg-soft-yellow/5 p-4">
              <div className="font-semibold text-charcoal">Collaboration</div>
              <p className="mt-1 text-slate-gray">
                Multi-user workflows for teams, partners, and production kitchens.
              </p>
            </div>

            <div className="rounded-xl border border-light-gray bg-soft-yellow/5 p-4">
              <div className="font-semibold text-charcoal">Advanced R&D</div>
              <p className="mt-1 text-slate-gray">
                Next-level experimentation once export and collab are locked in.
              </p>
            </div>
          </div>
        </section>

        {/* HOW USERS SHAPE RUOTH */}
        <section className="rounded-2xl border border-light-gray bg-white shadow-sm p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <ChatBubbleLeftRightIcon className="w-6 h-6 text-charcoal" />
            <h2 className="text-lg sm:text-xl font-semibold text-charcoal">
              Your voice matters here
            </h2>
          </div>

          <p className="mt-3 text-sm text-slate-gray max-w-3xl">
            You’re in the early room. What you use, request, and critique here directly shapes what ships next.
            That’s the deal.
          </p>

          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              variant="primary"
              onClick={() => window.open('https://instagram.com/ruothstore', '_blank')}
              className="justify-center gap-2"
            >
              Join @ruothstore
              <ArrowRightIcon className="w-4 h-4" />
            </Button>

            <Button
              type="button"
              variant="secondary"
              onClick={() => openLibrary()}
              className="justify-center gap-2"
            >
              Create your next recipe
              <SparklesIcon className="w-4 h-4" />
            </Button>
          </div>

          <p className="mt-3 text-xs text-slate-gray">
            Community is live on social for now (we’re keeping it focused). In-app community features will land after export and collaboration.
          </p>
        </section>

      </div>
    </div>
  )
}
